import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { BackButton } from "src/components/application/styled-components/Buttons";
import {
  AdhocKycAuth,
  AdhocKycContextResponse,
  AdhocKycInvalidReason,
  useGetAdhocKycContextQuery,
} from "src/store/features/api/adhocKyc";
import { AdhocKycError, AdhocKycSpinner } from "./AdhocKycShell";
import AdhocKycStepper from "./AdhocKycStepper";
import ConsentStep from "./ConsentStep";
import DetailsStep from "./DetailsStep";
import DoneStep from "./DoneStep";
import InvalidLinkPage from "./InvalidLinkPage";
import OnfidoStep from "./OnfidoStep";
import QuestionsStep from "./QuestionsStep";

export interface AdhocKycStepRouterProps {
  auth: AdhocKycAuth;
  homePath: string;
  /** Fired once the server reports the token dead, so the stored copy is dropped. */
  onInvalid: () => void;
}

/** How often the final screen re-reads the context while the DVS verdict is outstanding, and for how long. */
export const ADHOC_KYC_VERDICT_POLL_MS = 4_000;
export const ADHOC_KYC_VERDICT_WAIT_MS = 120_000;

/**
 * On the final screen with no verdict yet (DataZoo answers in seconds, but
 * the job is asynchronous): keep polling so the customer sees the result —
 * and the retry offer on a no-match — without a second email.
 */
export const isWaitingForVerdict = (context: AdhocKycContextResponse | undefined): boolean =>
  context?.valid === true &&
  context.enabled !== false &&
  context.step === "done" &&
  (context.verificationStatus === undefined || context.verificationStatus === "pending");

/** Which step component renders for a valid, enabled context. */
export const renderStep = (
  auth: AdhocKycAuth,
  context: AdhocKycContextResponse,
  homePath: string,
  refetch: () => void,
  checking: boolean,
): JSX.Element => {
  switch (context.step) {
    case "consent":
      return <ConsentStep auth={auth} context={context} />;
    case "details":
    case "update":
      return <DetailsStep auth={auth} context={context} />;
    case "onfido":
      return <OnfidoStep auth={auth} context={context} />;
    case "questions":
      return <QuestionsStep auth={auth} context={context} onRefresh={refetch} />;
    case "done":
      return <DoneStep auth={auth} context={context} homePath={homePath} checking={checking} />;
    default:
      return <InvalidLinkPage reason="error" homePath={homePath} />;
  }
};

/**
 * Pulls the context and renders whatever screen the server says is next.
 * Every step's mutation invalidates the context tag, so this re-renders with
 * the new `step` after each POST — no client-side state machine.
 */
export default function AdhocKycStepRouter({
  auth,
  homePath,
  onInvalid,
}: AdhocKycStepRouterProps): JSX.Element {
  // Poll while the verdict is outstanding, for at most ADHOC_KYC_VERDICT_WAIT_MS;
  // after that the plain "all done" card shows with Verification: Running.
  // What the customer SEES (`checking`) is derived from the data on the same
  // render it arrives — never from lagged state — so the first paint of a
  // pending verdict is the spinner, not an "all done" card they could leave
  // on (Bugbot, PR #3609). Only the poll interval, which cannot depend on the
  // hook's own result, follows one render behind.
  const [timedOut, setTimedOut] = useState(false);
  const [polling, setPolling] = useState(false);
  const { data, isLoading, isError, refetch } = useGetAdhocKycContextQuery(auth, {
    pollingInterval: polling ? ADHOC_KYC_VERDICT_POLL_MS : 0,
  });
  const invalid = data?.valid === false;
  const waiting = isWaitingForVerdict(data);
  const checking = waiting && !timedOut;

  useEffect(() => {
    if (invalid) onInvalid();
  }, [invalid, onInvalid]);

  useEffect(() => {
    if (!waiting) {
      setTimedOut(false);
      return undefined;
    }
    const timer = setTimeout(() => setTimedOut(true), ADHOC_KYC_VERDICT_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waiting]);

  useEffect(() => {
    setPolling(checking);
  }, [checking]);

  if (isLoading) return <AdhocKycSpinner message="Loading your verification" />;

  if (isError || !data) {
    return (
      <Box data-testid="adhoc-kyc-load-error">
        <AdhocKycError message="We couldn't load your verification. Please check your connection and try again." />
        <BackButton fullWidth onClick={() => refetch()} data-testid="adhoc-kyc-load-retry">
          Try again
        </BackButton>
      </Box>
    );
  }

  if (!data.valid) {
    // A stored token can expire between visits; hand it over so the expired
    // page can still offer "Send me a fresh link" (the resend names the row).
    return (
      <InvalidLinkPage
        reason={(data.reason || "invalid") as AdhocKycInvalidReason}
        token={auth.token}
        homePath={homePath}
      />
    );
  }

  if (data.enabled === false) {
    return <InvalidLinkPage reason="disabled" homePath={homePath} />;
  }

  return (
    <Box data-testid="adhoc-kyc-step-router" data-step={data.step}>
      <AdhocKycStepper
        step={data.step || "consent"}
        idCheckRequired={data.detailsOutcome === "updated"}
      />
      {renderStep(auth, data, homePath, refetch, checking)}
    </Box>
  );
}
